import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABEL = { low: "Low intent", medium: "Medium intent", high: "High intent" };
const CATEGORY_TONE = {
  low: "bg-[#1F1F1F] text-[#888] border-[#2A2A2A]",
  medium: "bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/30",
  high: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30",
};

const KIND_TONE = {
  chat: "bg-[#64D2FF]/15 text-[#64D2FF] border-[#64D2FF]/30",
  behavior: "bg-[#BF5AF2]/15 text-[#BF5AF2] border-[#BF5AF2]/30",
};

const THRESHOLD_ORDER = ["hot", "warm_proactive", "warm_soft_bubble"];
const THRESHOLD_LABELS = {
  hot: "Hot lead (triggers handoff)",
  warm_proactive: "Warm — Proactive open",
  warm_soft_bubble: "Warm — Soft pop-up bubble",
};

function BudgetBar({ kind, sum, max = 100 }) {
  const pct = Math.min(100, Math.round((sum / max) * 100));
  const exceeded = sum > max;
  const close = !exceeded && sum >= max * 0.85;
  const barTone = exceeded ? "bg-[#FF3B30]" : close ? "bg-[#FFD60A]" : "bg-[#32D74B]";
  const labelTone = exceeded ? "text-[#FF3B30]" : close ? "text-[#FFD60A]" : "text-[#32D74B]";
  return (
    <div className="flex flex-col items-end gap-1 min-w-[200px]" data-testid={`budget-${kind}`}>
      <div className={`text-[11px] font-mono ${labelTone}`}>
        Weight sum: <span data-testid={`budget-sum-${kind}`}>{sum}</span> / {max}
      </div>
      <div className="w-full h-1.5 bg-[#1A1A1A] rounded overflow-hidden">
        <div
          className={`h-full ${barTone} transition-all`}
          style={{ width: `${pct}%` }}
          data-testid={`budget-bar-${kind}`}
        />
      </div>
      {exceeded && (
        <div className="text-[10px] text-[#FF3B30] leading-snug" data-testid={`budget-warning-${kind}`}>
          Exceeds cap. Per-session {kind} score is hard-capped at 100 — additional points are silently absorbed.
        </div>
      )}
      {close && (
        <div className="text-[10px] text-[#FFD60A]/80 leading-snug">
          Approaching the 100-point cap. Plan ahead — events above the cap will be silently absorbed.
        </div>
      )}
    </div>
  );
}

function RuleRow({ rule, draft, onChange }) {
  const pts = draft.points ?? rule.points;
  const isOneTime = draft.is_one_time ?? rule.is_one_time;
  const dirty = (draft.points !== undefined && draft.points !== rule.points) || (draft.is_one_time !== undefined && draft.is_one_time !== rule.is_one_time);

  return (
    <div className={`grid grid-cols-12 gap-3 items-center py-2 px-3 border-b border-[#1A1A1A] last:border-b-0 ${dirty ? "bg-[#E85D24]/5" : ""}`} data-testid={`rule-row-${rule.event_type}`}>
      <div className="col-span-5">
        <div className="flex items-center gap-2">
          <code className="text-xs font-mono text-white">{rule.event_type}</code>
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${KIND_TONE[rule.kind] || ""}`}>{rule.kind}</span>
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${CATEGORY_TONE[rule.category] || ""}`}>{rule.category}</span>
        </div>
        <p className="text-[11px] text-[#666] mt-0.5 leading-snug">{rule.description}</p>
      </div>
      <div className="col-span-3 flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={100}
          value={pts}
          onChange={(e) => onChange(rule.event_type, { points: Number(e.target.value) })}
          data-testid={`points-input-${rule.event_type}`}
          className="w-20 bg-[#0A0A0A] border border-[#1F1F1F] focus:border-[#E85D24] rounded px-2 py-1.5 text-xs text-white font-mono outline-none"
        />
        <span className="text-[11px] text-[#666]">pts</span>
      </div>
      <div className="col-span-4 flex items-center gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!isOneTime}
            onChange={(e) => onChange(rule.event_type, { is_one_time: e.target.checked })}
            data-testid={`onetime-checkbox-${rule.event_type}`}
            className="accent-[#E85D24] w-3.5 h-3.5"
          />
          <span className="text-[11px] text-[#A0A0A0]">One-time per session</span>
        </label>
      </div>
    </div>
  );
}

function ThresholdRow({ threshold, draft, onChange }) {
  const value = draft.min_score ?? threshold.min_score;
  const dirty = draft.min_score !== undefined && draft.min_score !== threshold.min_score;
  return (
    <div className={`grid grid-cols-12 gap-3 items-center py-3 px-3 border-b border-[#1A1A1A] last:border-b-0 ${dirty ? "bg-[#E85D24]/5" : ""}`} data-testid={`threshold-row-${threshold.name}`}>
      <div className="col-span-7">
        <div className="text-sm text-white font-medium">{THRESHOLD_LABELS[threshold.name] || threshold.name}</div>
        <p className="text-[11px] text-[#666] mt-0.5 leading-snug">{threshold.description}</p>
      </div>
      <div className="col-span-5 flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange(threshold.name, Number(e.target.value))}
          data-testid={`threshold-slider-${threshold.name}`}
          className="flex-1 accent-[#E85D24]"
        />
        <input
          type="number"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange(threshold.name, Number(e.target.value))}
          data-testid={`threshold-input-${threshold.name}`}
          className="w-16 bg-[#0A0A0A] border border-[#1F1F1F] focus:border-[#E85D24] rounded px-2 py-1.5 text-xs text-white font-mono outline-none"
        />
        <span className="text-[11px] text-[#666]">≥</span>
      </div>
    </div>
  );
}

export default function ScoringRulesTab() {
  const [rules, setRules] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [weightSums, setWeightSums] = useState({ chat: 0, behavior: 0 });
  const [ruleDrafts, setRuleDrafts] = useState({});
  const [thresDrafts, setThresDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scoring-rules`);
      setRules(res.data.rules);
      setThresholds(res.data.thresholds);
      setWeightSums(res.data.weight_sums || { chat: 0, behavior: 0 });
    } catch (err) {
      console.error("Failed to load scoring rules:", err);
      toast.error(err.response?.status === 403 ? "Admin access required" : "Could not load scoring rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const groupedRules = useMemo(() => {
    const groups = { chat: [], behavior: [] };
    for (const r of rules) {
      if (groups[r.kind]) groups[r.kind].push(r);
    }
    return groups;
  }, [rules]);

  // Live weight-sum per category, applying any unsaved draft changes on top of
  // the persisted points. This is the number the admin sees in the "budget" bar.
  const liveSums = useMemo(() => {
    const sums = { chat: 0, behavior: 0 };
    for (const r of rules) {
      const draftPts = ruleDrafts[r.event_type]?.points;
      const pts = draftPts !== undefined ? draftPts : r.points;
      if (sums[r.kind] !== undefined) sums[r.kind] += Number(pts) || 0;
    }
    return sums;
  }, [rules, ruleDrafts]);

  const sortedThresholds = useMemo(() => {
    return [...thresholds].sort((a, b) => THRESHOLD_ORDER.indexOf(a.name) - THRESHOLD_ORDER.indexOf(b.name));
  }, [thresholds]);

  const onRuleChange = (eventType, patch) => {
    setRuleDrafts((d) => ({ ...d, [eventType]: { ...d[eventType], ...patch } }));
  };

  const onThresholdChange = (name, value) => {
    setThresDrafts((d) => ({ ...d, [name]: { ...d[name], min_score: value } }));
  };

  const dirtyRules = Object.entries(ruleDrafts).filter(([et, d]) => {
    const original = rules.find(r => r.event_type === et);
    if (!original) return false;
    return (d.points !== undefined && d.points !== original.points) || (d.is_one_time !== undefined && d.is_one_time !== original.is_one_time);
  });

  const dirtyThresholds = Object.entries(thresDrafts).filter(([n, d]) => {
    const original = thresholds.find(t => t.name === n);
    if (!original) return false;
    return d.min_score !== undefined && d.min_score !== original.min_score;
  });

  const dirty = dirtyRules.length > 0 || dirtyThresholds.length > 0;

  const handleSave = async () => {
    if (!dirty) {
      toast.info("No changes to save");
      return;
    }
    // Threshold sanity: hot > warm_proactive >= warm_soft_bubble
    const merged = (name) => {
      const orig = thresholds.find(t => t.name === name)?.min_score ?? 0;
      return thresDrafts[name]?.min_score ?? orig;
    };
    const hot = merged("hot"), wp = merged("warm_proactive"), wsb = merged("warm_soft_bubble");
    if (!(hot > wp && wp >= wsb)) {
      toast.error(`Thresholds must satisfy: Hot (${hot}) > Warm Proactive (${wp}) ≥ Warm Soft Bubble (${wsb}).`);
      return;
    }
    // Budget warning — over-budget is allowed (the backend hard-caps at 100), but
    // we surface it so the admin knows the implication.
    if (liveSums.chat > 100 || liveSums.behavior > 100) {
      const overs = [];
      if (liveSums.chat > 100) overs.push(`Chat (${liveSums.chat}/100)`);
      if (liveSums.behavior > 100) overs.push(`Behaviour (${liveSums.behavior}/100)`);
      const proceed = window.confirm(`Heads-up — ${overs.join(" and ")} exceed the 100-point cap. Any signal points above the cap will be silently absorbed at scoring time. The audit log still records full intent. Save anyway?`);
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const ruleUpdates = dirtyRules.map(([event_type, d]) => {
        const original = rules.find(r => r.event_type === event_type);
        return {
          event_type,
          points: d.points ?? original.points,
          is_one_time: d.is_one_time ?? original.is_one_time
        };
      });
      const thresholdUpdates = dirtyThresholds.map(([name, d]) => ({ name, min_score: d.min_score }));
      const res = await axios.put(`${API}/scoring-rules`, { rules: ruleUpdates, thresholds: thresholdUpdates });
      setRules(res.data.rules);
      setThresholds(res.data.thresholds);
      setWeightSums(res.data.weight_sums || { chat: 0, behavior: 0 });
      setRuleDrafts({});
      setThresDrafts({});
      toast.success(`Saved ${res.data.changes.rules} rule(s) + ${res.data.changes.thresholds} threshold(s). Live on next event.`);
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Save failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset ALL scoring rules and thresholds to factory defaults? Unsaved changes will be lost.")) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/scoring-rules/reset`);
      setRules(res.data.rules);
      setThresholds(res.data.thresholds);
      setWeightSums(res.data.weight_sums || { chat: 0, behavior: 0 });
      setRuleDrafts({});
      setThresDrafts({});
      toast.success("All scoring rules + thresholds reset to defaults.");
    } catch (err) {
      console.error("Reset failed:", err);
      toast.error("Reset failed: " + (err.response?.data?.error || err.message));
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="text-[#707070] text-sm" data-testid="scoring-rules-loading">Loading scoring rules…</div>;

  return (
    <div data-testid="scoring-rules-tab" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Scoring Rules</h2>
          <p className="text-xs text-[#888] mt-1 max-w-3xl leading-relaxed">
            Tune the points awarded for every chat signal and behaviour event, plus the hot/warm thresholds. Changes go live on the very next event — no restart. <strong className="text-[#FFD60A]">Tip:</strong> One-time flag means "count only on the first occurrence per session" — essential for events like dwell-timers or page-views that fire repeatedly.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleReset}
            disabled={resetting || saving}
            data-testid="reset-scoring-btn"
            className="px-3 py-2 text-xs font-medium bg-[#1A1A1A] hover:bg-[#252525] border border-[#2A2A2A] hover:border-[#FF3B30] text-[#A0A0A0] hover:text-[#FF3B30] rounded transition-colors disabled:opacity-50"
          >
            {resetting ? "Resetting…" : "Reset to defaults"}
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid="save-scoring-btn"
            className="px-4 py-2 text-xs font-medium bg-[#E85D24] hover:bg-[#E85D24]/85 disabled:bg-[#333] disabled:cursor-not-allowed text-white rounded transition-colors"
          >
            {saving ? "Saving…" : dirty ? `Save (${dirtyRules.length + dirtyThresholds.length})` : "No changes"}
          </button>
        </div>
      </div>

      {/* Thresholds */}
      <section className="border border-[#1A1A1A] rounded">
        <header className="px-4 py-3 bg-[#0F0F0F] border-b border-[#1A1A1A]">
          <h3 className="text-sm font-semibold text-white">Tier Thresholds</h3>
          <p className="text-[11px] text-[#666] mt-0.5">Must satisfy: Hot &gt; Warm Proactive ≥ Warm Soft Bubble.</p>
        </header>
        <div>
          {sortedThresholds.map((t) => (
            <ThresholdRow key={t.name} threshold={t} draft={thresDrafts[t.name] || {}} onChange={onThresholdChange} />
          ))}
        </div>
      </section>

      {/* Chat signals */}
      <section className="border border-[#1A1A1A] rounded">
        <header className="px-4 py-3 bg-[#0F0F0F] border-b border-[#1A1A1A] flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Chat signals</h3>
            <p className="text-[11px] text-[#666] mt-0.5">Triggered by Arya from analysing the visitor's messages and her own replies. Per-session chat score is hard-capped at 100.</p>
          </div>
          <BudgetBar kind="chat" sum={liveSums.chat} max={100} />
        </header>
        <div>
          {groupedRules.chat.map((r) => (
            <RuleRow key={r.event_type} rule={r} draft={ruleDrafts[r.event_type] || {}} onChange={onRuleChange} />
          ))}
        </div>
      </section>

      {/* Behaviour events */}
      <section className="border border-[#1A1A1A] rounded">
        <header className="px-4 py-3 bg-[#0F0F0F] border-b border-[#1A1A1A] flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Behaviour events</h3>
            <p className="text-[11px] text-[#666] mt-0.5">Fired by the widget on page-load, dwell, hover, scroll, click etc. Per-session behaviour score is hard-capped at 100.</p>
          </div>
          <BudgetBar kind="behavior" sum={liveSums.behavior} max={100} />
        </header>
        <div>
          {groupedRules.behavior.map((r) => (
            <RuleRow key={r.event_type} rule={r} draft={ruleDrafts[r.event_type] || {}} onChange={onRuleChange} />
          ))}
        </div>
      </section>

      <div className="text-[11px] text-[#666] leading-relaxed border border-[#1A1A1A] rounded p-3 bg-[#0A0A0A]">
        <strong className="text-[#888]">How this works:</strong> The backend caches all rules + 3 thresholds in memory. The cache reloads after every save, so the very next /api/score or /api/chat call uses the new values. <strong className="text-[#888]">Two-category scoring:</strong> chat signals and behaviour events each accrue into their own 0-100 score; total session score is 0-200. A lead is promoted to a tier if <em>either</em> category meets that tier's threshold.
      </div>
    </div>
  );
}
